import type { Conversation } from '@nucleus-commerce/api-client/admin';

type Named = Pick<Conversation, 'visitor' | 'customer' | 'email'>;

/** A stable short label for an anonymous visitor: the last four hex digits of their id. */
export function visitorTag(visitorId: string): string {
  return visitorId
    .replace(/[^0-9a-f]/gi, '')
    .slice(-4)
    .toUpperCase();
}

/**
 * The shopper as staff should see them: the customer's name, the name or email they typed, or
 * `null` when anonymous (the caller then shows "Visitor 4F2A" from `visitorTag`).
 */
export function shopperName(conversation: Named): string | null {
  const customer = conversation.customer;
  const full = [customer?.firstName, customer?.lastName].filter(Boolean).join(' ').trim();
  // The first one that is set and not blank.
  const candidates = [
    full,
    conversation.visitor?.displayName,
    customer?.email,
    conversation.visitor?.email,
    conversation.email,
  ];
  return (
    candidates.find((value): value is string => typeof value === 'string' && value.trim() !== '') ??
    null
  );
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? [parts[0], parts.at(-1)] : [parts[0]];
  return letters
    .map((part) => part?.[0] ?? '')
    .join('')
    .toUpperCase();
}

/** Minutes since the shopper's last unanswered message, for the "needs reply" badge. */
export function waitingMinutes(
  conversation: Pick<Conversation, 'status' | 'lastVisitorMessageAt' | 'lastStaffMessageAt'>,
  now: number,
): number | null {
  if (conversation.status !== 'open' || !conversation.lastVisitorMessageAt) return null;
  if (
    conversation.lastStaffMessageAt &&
    conversation.lastStaffMessageAt >= conversation.lastVisitorMessageAt
  )
    return null;
  return Math.max(0, Math.floor((now - Date.parse(conversation.lastVisitorMessageAt)) / 60_000));
}
